import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Layer } from "../actions/Layers/constants";
import useConfig from "../actions/Tenants/config/configHook";
import { useConnectivity } from "../offline/connectivityMonitor";
import { isOfflineChecksEnabled } from "../offline/db";
import { startOfflineCheck } from "../offline/startOfflineCheck";
import { toast } from "../utils/toast";
import useNavigateFrom from "../utils/useNavigateFrom";
import { useCreateLayerMutation } from "./mutations/useLayerMutations";
import { useDefaultLayer } from "./useDefaultLayer";

export const useLayerModal = () => {
    const { navigateTo } = useNavigateFrom();
    const params = useParams();
    const tenantId = params.tenant_id as string;
    const createLayerMutation = useCreateLayerMutation();

    const [layerModalOpen, setLayerModal] = useState<false | { withScanner: boolean }>(false);

    const [layer, setLayer] = useDefaultLayer();

    const config = useConfig();
    const { isOnline } = useConnectivity();
    const offlineMode = isOfflineChecksEnabled(config) && !isOnline;
    const layerConfig = useMemo(() => config.get_layer_config(layer), [config, layer?.type]);
    const useBarcodeScanner = config.use_barcode_scanner;

    const onCreateLayer = () => {
        createLayerMutation.mutateAsync({ tenantId, data: layer }).then((createdLayer) => {
            if (createdLayer) {
                // Keep the originating `from` (e.g. ripening room) alive across the pallet switch.
                navigateTo(`/layer/${createdLayer.id}/action/${layerConfig.start_action || "edit"}`, true);
            }
        });
    };

    const setValue = (field, value) => setLayer((object) => ({ ...object, [field]: value }));

    const onClose = () => setLayerModal(false);

    // * If the scanned/searched layer already exists, navigate to the layer page
    const setLayerSelect = async (select: Layer | Layer[], actionType?: string) => {
        const check = { location: actionType || "" };
        const location = Array.isArray(select) ? undefined : config.get_check_type_config(select, check);
        const urlAppendix = actionType && !!location ? `/action/start-${actionType}` : "";

        if (!Array.isArray(select) && select.id) {
            // Offline barcode-pick: same UX flow as online (autocomplete →
            // pick → navigate to start-check), but the navigation is
            // bracketed by an OfflineCheckSession so the start-check
            // route can render from local state instead of fetching from
            // the server. See offline/startOfflineCheck.ts for the full
            // session-creation contract.
            if (offlineMode && actionType && config.tenant_id) {
                const result = await startOfflineCheck({
                    label: select.label!,
                    layer: select,
                    checkType: actionType,
                    tenantId: config.tenant_id,
                });
                if (!result.ok) {
                    toast.error(result.reason);
                    return;
                }
                // Keep the originating `from` (e.g. ripening room) alive across the pallet switch.
                navigateTo(`/layer/${select.id}/action/start-${actionType}`, true);
                return;
            }
            navigateTo(`/layer/${select.id}${urlAppendix}`, true);
            return;
        }

        // * if it doesn't exist, open the create layer modal
        setLayer(select as Layer);
        setLayerModal({ withScanner: false });
    };

    return {
        layerModalOpen,
        setLayerModal,
        onCreateLayer,
        setValue,
        onClose,
        setLayerSelect,
        layerConfig,
        layer,
        setLayer,
        useBarcodeScanner,
    };
};
